import { useEffect, useMemo, useState, type ReactNode } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Bot,
  Check,
  Heading1,
  Heading2,
  Italic,
  LinkIcon,
  List,
  ListOrdered,
  Loader2,
  Pilcrow,
  Plus,
  RotateCcw,
  Sparkles,
  Strikethrough,
  Trash2,
  Underline as UnderlineIcon,
  WandSparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useAIProvider } from "@/hooks/use-ai-provider";
import { getRecommendedModel } from "@/ai/models";
import {
  generatePolicyAIEdit,
  type PolicyAIAction,
  type PolicyEditorAIContext,
} from "@/lib/policy-editor-ai";

interface PolicyRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  direction?: "ltr" | "rtl";
  placeholder?: string;
  policyContext: PolicyEditorAIContext;
  disabled?: boolean;
}

interface PendingAIResult {
  html: string;
  label: string;
  scope: "selection" | "document";
  from?: number;
  to?: number;
}

const selectionActions: Array<{ action: PolicyAIAction; label: string }> = [
  { action: "rewrite", label: "Rewrite" },
  { action: "clarify", label: "Make clearer" },
  { action: "formal", label: "Make formal" },
  { action: "shorten", label: "Shorten" },
  { action: "expand", label: "Expand" },
  { action: "grammar", label: "Fix grammar" },
  { action: "summarize", label: "Summarize" },
];

const documentActions: Array<{ action: PolicyAIAction; label: string }> = [
  { action: "improve", label: "Improve policy" },
  { action: "missing_section", label: "Draft missing section" },
  { action: "implementation_steps", label: "Add implementation steps" },
  { action: "find_gaps", label: "Fix policy gaps" },
];

const ToolbarButton = ({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant={active ? "default" : "ghost"}
        size="sm"
        disabled={disabled}
        onClick={onClick}
        className="h-8 w-8 shrink-0 px-0"
        aria-label={label}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

export function PolicyRichTextEditor({
  value,
  onChange,
  direction = "ltr",
  placeholder = "Enter the detailed policy content...",
  policyContext,
  disabled,
}: PolicyRichTextEditorProps) {
  const { currentProvider, availableProviders } = useAIProvider();
  const [aiOpen, setAiOpen] = useState(false);
  const [customInstruction, setCustomInstruction] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [pendingAIResult, setPendingAIResult] = useState<PendingAIResult | null>(null);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    [placeholder],
  );

  const editor = useEditor({
    extensions,
    content: value || "",
    editable: !disabled,
    editorProps: {
      attributes: {
        class:
          "policy-tiptap-content min-h-[560px] px-5 py-5 text-sm leading-7 text-foreground outline-none",
        dir: direction,
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.view.dom.setAttribute("dir", direction);
    editor.view.dom.style.textAlign = direction === "rtl" ? "right" : "left";
  }, [direction, editor]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next && current !== next) {
      editor.commands.setContent(next, false);
    }
    if (!next && current !== "<p></p>") {
      editor.commands.clearContent(false);
    }
  }, [editor, value]);

  const hasSelection = Boolean(editor && !editor.state.selection.empty);
  const hasAIKey = availableProviders.length > 0;

  const runAIAction = async (action: PolicyAIAction, label: string) => {
    if (!editor || aiLoading || disabled) return;

    setAiError(null);
    setPendingAIResult(null);

    if (!hasAIKey) {
      setAiError("Configure a Gemini or OpenAI API key in Settings to use AI editing.");
      return;
    }

    if (action === "custom" && !customInstruction.trim()) {
      setAiError("Enter an instruction for the AI edit.");
      return;
    }

    const from = editor.state.selection.from;
    const to = editor.state.selection.to;
    const selectedText = hasSelection ? editor.state.doc.textBetween(from, to, "\n") : "";
    const scope: PendingAIResult["scope"] = selectedText.trim() ? "selection" : "document";

    setAiLoading(true);
    try {
      const html = await generatePolicyAIEdit({
        action,
        provider: currentProvider,
        model: getRecommendedModel(currentProvider, "CHAT"),
        policyContext,
        documentText: editor.getText({ blockSeparator: "\n" }),
        selectedText,
        customInstruction,
      });

      if (!html.trim()) {
        throw new Error("AI returned an empty edit.");
      }

      setPendingAIResult({
        html,
        label,
        scope,
        from: scope === "selection" ? from : undefined,
        to: scope === "selection" ? to : undefined,
      });
      setAiOpen(false);
    } catch (error) {
      setAiError(error instanceof Error ? error.message : "AI edit failed.");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAIResult = () => {
    if (!editor || !pendingAIResult) return;
    if (pendingAIResult.scope === "selection" && pendingAIResult.from && pendingAIResult.to) {
      editor
        .chain()
        .focus()
        .setTextSelection({ from: pendingAIResult.from, to: pendingAIResult.to })
        .insertContent(pendingAIResult.html)
        .run();
    } else {
      editor.commands.setContent(pendingAIResult.html, true);
    }
    setPendingAIResult(null);
  };

  const insertAIResult = () => {
    if (!editor || !pendingAIResult) return;
    editor.chain().focus().insertContent(pendingAIResult.html).run();
    setPendingAIResult(null);
  };

  const addLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Paste a link", previousUrl || "https://");
    if (url === null) return;
    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  if (!editor) return null;

  return (
    <TooltipProvider>
      <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
        <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-2">
          <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <ToolbarButton label="Paragraph" active={editor.isActive("paragraph")} disabled={disabled} onClick={() => editor.chain().focus().setParagraph().run()}>
              <Pilcrow className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Heading 1" active={editor.isActive("heading", { level: 1 })} disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
              <Heading1 className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Heading 2" active={editor.isActive("heading", { level: 2 })} disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
              <Heading2 className="h-4 w-4" />
            </ToolbarButton>
            <div className="mx-1 h-6 w-px bg-border" />
            <ToolbarButton label="Bold" active={editor.isActive("bold")} disabled={disabled} onClick={() => editor.chain().focus().toggleBold().run()}>
              <Bold className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Italic" active={editor.isActive("italic")} disabled={disabled} onClick={() => editor.chain().focus().toggleItalic().run()}>
              <Italic className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Underline" active={editor.isActive("underline")} disabled={disabled} onClick={() => editor.chain().focus().toggleUnderline().run()}>
              <UnderlineIcon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Strikethrough" active={editor.isActive("strike")} disabled={disabled} onClick={() => editor.chain().focus().toggleStrike().run()}>
              <Strikethrough className="h-4 w-4" />
            </ToolbarButton>
            <div className="mx-1 h-6 w-px bg-border" />
            <ToolbarButton label="Bullet list" active={editor.isActive("bulletList")} disabled={disabled} onClick={() => editor.chain().focus().toggleBulletList().run()}>
              <List className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Numbered list" active={editor.isActive("orderedList")} disabled={disabled} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
              <ListOrdered className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Link" active={editor.isActive("link")} disabled={disabled} onClick={addLink}>
              <LinkIcon className="h-4 w-4" />
            </ToolbarButton>
            <div className="mx-1 h-6 w-px bg-border" />
            <ToolbarButton label="Align left" active={editor.isActive({ textAlign: "left" })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign("left").run()}>
              <AlignLeft className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Align center" active={editor.isActive({ textAlign: "center" })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign("center").run()}>
              <AlignCenter className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Align right" active={editor.isActive({ textAlign: "right" })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign("right").run()}>
              <AlignRight className="h-4 w-4" />
            </ToolbarButton>
          </div>

          <Popover open={aiOpen} onOpenChange={setAiOpen}>
            <PopoverTrigger asChild>
              <Button type="button" size="sm" disabled={disabled || aiLoading} className="h-8 gap-1.5">
                {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                AI Help
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[360px] p-3">
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">AI editing</p>
                    <Badge variant={hasSelection ? "secondary" : "outline"} className="text-[11px]">
                      {hasSelection ? "Selection" : "Full document"}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Review AI output before applying it to the policy.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {(hasSelection ? selectionActions : documentActions).map((item) => (
                    <Button
                      key={item.action}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-start"
                      disabled={aiLoading}
                      onClick={() => runAIAction(item.action, item.label)}
                    >
                      <WandSparkles className="mr-2 h-3.5 w-3.5" />
                      {item.label}
                    </Button>
                  ))}
                </div>

                <div className="space-y-2">
                  <Textarea
                    value={customInstruction}
                    onChange={(event) => setCustomInstruction(event.target.value)}
                    placeholder="Ask AI to add a reporting clause, rewrite for SOC 2, or tighten responsibilities..."
                    className="min-h-[88px] resize-none text-sm"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    disabled={aiLoading || !customInstruction.trim()}
                    onClick={() => runAIAction("custom", "Custom AI edit")}
                  >
                    <Bot className="mr-2 h-4 w-4" />
                    Run custom edit
                  </Button>
                </div>

                {aiError && <p className="text-xs text-destructive">{aiError}</p>}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {aiError && !aiOpen && (
          <div className="border-b bg-destructive/10 px-4 py-2 text-sm text-destructive">{aiError}</div>
        )}

        {pendingAIResult && (
          <div className="border-b bg-primary/5 p-3">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <p className="text-sm font-medium">{pendingAIResult.label}</p>
                  <Badge variant="outline" className="text-[11px]">
                    {pendingAIResult.scope === "selection" ? "Selection replacement" : "Document draft"}
                  </Badge>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button type="button" size="sm" onClick={applyAIResult}>
                    <Check className="mr-1.5 h-4 w-4" />
                    Apply
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={insertAIResult}>
                    <Plus className="mr-1.5 h-4 w-4" />
                    Insert
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setPendingAIResult(null)}>
                    <Trash2 className="mr-1.5 h-4 w-4" />
                    Discard
                  </Button>
                </div>
              </div>
              <div
                className="max-h-48 overflow-auto rounded-md border bg-background px-3 py-2 text-sm leading-6"
                dangerouslySetInnerHTML={{ __html: pendingAIResult.html }}
              />
            </div>
          </div>
        )}

        <EditorContent
          editor={editor}
          className={cn(
            "bg-background",
            "prose prose-sm max-w-none dark:prose-invert",
            "[&_.ProseMirror-focused]:outline-none",
            "[&_.is-editor-empty:first-child:before]:pointer-events-none [&_.is-editor-empty:first-child:before]:float-left [&_.is-editor-empty:first-child:before]:h-0 [&_.is-editor-empty:first-child:before]:text-muted-foreground/60 [&_.is-editor-empty:first-child:before]:content-[attr(data-placeholder)]",
            direction === "rtl" && "text-right",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
          <span>{editor.getText().length} characters</span>
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={disabled} onClick={() => editor.chain().focus().undo().run()}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Undo
          </Button>
        </div>
      </div>
    </TooltipProvider>
  );
}
